import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FaCheckCircle, FaCloudUploadAlt, FaCog, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CardModal from '../CardModal';
import CustomScrollRestoration from '../CustomScrollRestoration';
import { ModeratorActionProvider } from '../../ModeratorActionContext';
import { useUser } from '../../UserContext';
import ModuleEditModal from './ModuleEditModal';
import ModuleManager from './ModuleManager';
import ModuleRenderer from './ModuleRenderer';
import useSetEditor from './hooks/useSetEditor';
import './SlidePanel.css';
import './Lightbox.css';
import './SetFonts.css';
import './SetTest.css';
import { IoIosArrowBack } from "react-icons/io";
import { Link } from 'react-router-dom';
import axios from 'axios';

const PANEL_WIDTH = '400px';

function SaveIndicator({ status, onRetry }) {
  if (status === 'idle') return null;
  const states = {
    pending: [<FaCloudUploadAlt />, 'Unsaved changes', 'secondary'],
    saving: [<Spinner animation="border" size="sm" />, 'Saving...', 'primary'],
    saved: [<FaCheckCircle />, 'All changes saved', 'success'],
    error: [<FaExclamationTriangle />, 'Save failed', 'danger'],
  };
  const [icon, label, variant] = states[status] || states.error;
  return (
    <button type="button" className={`set-save-status text-${variant}`} onClick={status === 'error' ? onRetry : undefined}>
      {icon}<span>{label}</span>{status === 'error' && <small>Retry</small>}
    </button>
  );
}

export default function SetTest() {
  const [searchParams] = useSearchParams();
  const { setId: routeSetId } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const setId = routeSetId || searchParams.get('setId');
  const selectedCard = searchParams.get('selectedCard');
  const [definitions, setDefinitions] = useState({});
  const [definitionsLoading, setDefinitionsLoading] = useState(true);
  const [definitionError, setDefinitionError] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState('');
  const { modules, updateModules, isLoading, canEdit, saveStatus, error, persist } = useSetEditor(setId);

  useEffect(() => {
    // const token = localStorage.getItem('token');

    // Attempt to retrieve the token from localStorage
    let token = null;

    // If the token does not exist, check for 'hcardss' in cookies
    if (!token) {
      const cookies = document.cookie.split('; ');
      const hcardssCookie = cookies.find(cookie => cookie.startsWith('hcardss='));
      //console.log(cookies);
      if (hcardssCookie) {
        token = hcardssCookie.split('=')[1]; // Extract the token value from 'hcardss' cookie
      }
    }

    // Function to delete the 'hcardss' cookie
    const deleteCookie = (name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    };

    if (token) {
      axios.post('/api/auth/auth.php', { action: 'check', token: token })
        .then(response => {
          const hc = response.data.hc;
          //console.log(hc);
          if (hc.loggedin) {
            setUser({
              userid: hc.userid,
              username: hc.username,
              rank: hc.rank,
              mod: hc.mod,
              profile_picture: hc.picture,
              supIcon: hc.supIcon,
              supColor: hc.supColor,
              HCCardCount: hc.hccardsmade
            });
          } else {
            localStorage.removeItem('token');
            deleteCookie('hcardss');
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          deleteCookie('hcardss');
        });
    }
  }, []);

  useEffect(() => {
    let active = true;
    const loadDefinitions = async () => {
      for (const url of ['/newsets/modules_new.json?v=1', '/newsets/modules_new.json?v=1']) {
        const response = await fetch(url);
        if (response.ok) return response.json();
      }
      throw new Error('Module definitions could not be loaded.');
    };
    loadDefinitions().then((payload) => {
      if (active) setDefinitions(payload);
    }).catch((loadError) => {
      if (active) setDefinitionError(loadError.message);
    }).finally(() => {
      if (active) setDefinitionsLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('set-page-no-scroll', Boolean(selectedCard || lightboxUrl));
    return () => document.body.classList.remove('set-page-no-scroll');
  }, [lightboxUrl, selectedCard]);

  const closeCard = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('selectedCard');
    navigate({ search: next.toString() }, { replace: true });
  }, [navigate, searchParams]);

  const editingModule = editingIndex === null ? null : modules[editingIndex];
  const editingDefinition = editingModule ? definitions[editingModule.type] : null;
  const pageError = definitionError || error;
  const loading = isLoading || definitionsLoading;
  const editorStyle = useMemo(() => ({ '--panel-width': PANEL_WIDTH }), []);

  return (
    <div className="set-test-container set-editor-shell" style={editorStyle}>
      {loading && <div className="set-loading-overlay"><div className="set-loading-content"><Spinner animation="border" /><h4>Loading set page...</h4></div></div>}
      {pageError && <Alert variant="danger" className="set-page-alert">{pageError}</Alert>}

      {(canEdit && !panelOpen) && (
        <Button className="set-settings-toggle shadow" variant="light" onClick={() => setPanelOpen((open) => !open)}
          aria-controls="set-module-manager-panel" aria-expanded={panelOpen} title={panelOpen ? 'Close page editor' : 'Edit page'}>
          {panelOpen ? <FaTimes /> : <FaCog />}
        </Button>
      )}

<aside id="set-module-manager-panel" className={`set-manager-panel bg-light ${panelOpen ? 'set-is-open' : ''}`}>
  <header className="set-manager-panel-header d-flex align-items-center justify-content-between">
    {/* Column 1: Back Button */}
    <div className="me-2">
      <Link to="/sets/" className="btn btn-light btn-sm d-flex align-items-center" aria-label="Back to sets">
        <IoIosArrowBack />
      </Link>
    </div>

    {/* Column 2: Title & Subtitle */}
    <div className="text-black flex-grow-1">
      <h5 className="mb-0">Set page editor</h5>
      <small className="text-muted">Arrange and edit page sections</small>
    </div>

    {/* Column 3: Close Button */}
    <div>
      <Button variant="light" size="sm" onClick={() => setPanelOpen(false)} aria-label="Close editor">
        <FaTimes />
      </Button>
    </div>
  </header>

  <div className="set-manager-panel-content">
    <ModuleManager 
      selectedModules={modules} 
      availableModules={definitions} 
      onModulesChange={updateModules}
      onRequestEdit={setEditingIndex} 
      isLoadingDefinitions={definitionsLoading} 
    />
    {/* <Button variant="primary" className="w-100 mt-3" onClick={persist} disabled={!setId || saveStatus === 'saving'}>
      {saveStatus === 'saving' ? 'Saving...' : 'Save now'}
    </Button> */}
  </div>
</aside>

      <main className="set-main-content">
        {panelOpen && <button className="set-panel-overlay" onClick={() => setPanelOpen(false)} aria-label="Close editor" />}
        <ModuleRenderer modules={modules} currentSetId={setId} isOwner={canEdit} onEditModule={setEditingIndex}
          availableModules={definitions} onImageClick={setLightboxUrl} />
        {!canEdit && !loading && modules.length === 0 && !pageError && <p className="set-empty-page">This set page has no sections yet.</p>}
      </main>

      {editingModule && editingDefinition && (
        <ModuleEditModal show handleClose={() => setEditingIndex(null)}
          handleSave={(nextModule) => {
            updateModules((current) => current.map((module, index) => index === editingIndex ? nextModule : module));
            setEditingIndex(null);
          }}
          moduleDefinition={editingDefinition} moduleData={editingModule} setId={setId} />
      )}

      <ModeratorActionProvider>
        {selectedCard && <CardModal card={selectedCard} user={user} onClose={closeCard} />}
        <CustomScrollRestoration />
      </ModeratorActionProvider>
      <SaveIndicator status={saveStatus} onRetry={persist} />

      {lightboxUrl && (
        <div className="set-lightbox-overlay" role="dialog" aria-modal="true" aria-label="Image preview" onClick={() => setLightboxUrl('')}>
          <button className="set-lightbox-close" onClick={() => setLightboxUrl('')} aria-label="Close image"><FaTimes /></button>
          <div className="set-lightbox-content" onClick={(event) => event.stopPropagation()}><img src={lightboxUrl} alt="Enlarged view" /></div>
        </div>
      )}
    </div>
  );
}
